import React from 'react'
import { FormInputRow, Input } from '@emerald-works-nova/components'
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Switch, FormControlLabel } from '@mui/material'
import { Controller } from 'react-hook-form'
import { useRHFInitialValues } from '../../hooks'
import Image from './components/image'
import { META_SKILL_OPTIONS } from '../../constants/meta-skill-options'

const SkillBiteDetailsForm = ({ form, errors = {}, smallFormTitles = false, isReadOnly }) => {
  const {
    control,
    setValue,
    watch,
    initialValues = {}
  } = form

  useRHFInitialValues(initialValues, setValue)

  const selectedMetaSkill = META_SKILL_OPTIONS.find(metaSkill => metaSkill.value === watch('metaSkill'))
  const skillOptions = selectedMetaSkill ? selectedMetaSkill.skills : []

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormInputRow
          title='Expert Skill Bite'
          smallFormTitles={smallFormTitles}
          description='Turn the toggle ON to mark this Skill Bite as an Expert Skill Bite.'
          InputComponent={
            <Controller
              control={control}
              name='expert'
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  label='Is this an Expert Skill Bite?'
                  labelPlacement='start'
                  control={<Switch checked={value} color='primary' disabled={isReadOnly} onChange={(ev) => onChange(ev.target.checked)} />}
                />
              )}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Title'
          smallFormTitles={smallFormTitles}
          description='This is the full name of the Skill Bites Course, as you want it to appear on screen. (Optimised for SEO)'
          InputComponent={
            <Input
              autoFocus
              control={control}
              rules={{ required: true }}
              label='Title'
              name='name'
              valid={errors.name === undefined}
              showErrors
              error='Title is required'
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Short Description'
          smallFormTitles={smallFormTitles}
          description='Descriptive text optimised for SEO. Compelling, active description, directly relevant to the content to be used in the Skill Bites card. Use the 155 character limit.'
          InputComponent={
            <Input
              control={control}
              rules={{ required: true }}
              label='Short Description'
              name='shortDescription'
              valid={errors.shortDescription === undefined}
              showErrors
              error='Short Description is required'
              maxLength={155}
              multiline
              rows={2}
              showCounter
              showHelperTextForCounter
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Landing Page Description'
          smallFormTitles={smallFormTitles}
          description='Comprehensive introductory text to explain the course and its objective to the user. Use the X character limit.'
          InputComponent={
            <Input
              control={control}
              rules={{ required: true }}
              label='Landing Page Description'
              showErrors
              error='Landing Page Description is required'
              name='description'
              multiline
              rows={8}
              valid={errors.description === undefined}
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
      <Image
        form={form}
        imgPrefix='skill-bite-'
        isReadOnly={isReadOnly}
      />
      <Grid item xs={12}>
        <FormInputRow
          title='Meta Skill'
          smallFormTitles={smallFormTitles}
          description='Optional. Link this skill bite to a meta skill from the Accelerate journey for personalized recommendations.'
          InputComponent={
            <Controller
              name='metaSkill'
              control={control}
              defaultValue={initialValues.metaSkill || ''}
              render={({ field: { onChange, value } }) => (
                <FormControl variant='outlined' fullWidth>
                  <InputLabel id='meta-skill-select-label'>Select Meta Skill (Optional)</InputLabel>
                  <Select
                    labelId='meta-skill-select-label'
                    id='meta-skill-select'
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value)
                      // Reset skill when meta skill changes
                      setValue('skill', '')
                    }}
                    label='Select Meta Skill (Optional)'
                    disabled={isReadOnly}
                  >
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {META_SKILL_OPTIONS.map((metaSkill) => (
                      <MenuItem key={metaSkill.value} value={metaSkill.value}>
                        {metaSkill.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Skill'
          smallFormTitles={smallFormTitles}
          description='Optional. Select the specific skill within the chosen meta skill category.'
          InputComponent={
            <Controller
              name='skill'
              control={control}
              defaultValue={initialValues.skill || ''}
              render={({ field: { onChange, value } }) => (
                <FormControl variant='outlined' fullWidth>
                  <InputLabel id='skill-select-label'>Select Skill (Optional)</InputLabel>
                  <Select
                    labelId='skill-select-label'
                    id='skill-select'
                    value={value}
                    onChange={onChange}
                    label='Select Skill (Optional)'
                    disabled={isReadOnly || !watch('metaSkill')}
                  >
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {skillOptions.map((skill) => (
                      <MenuItem key={skill.value} value={skill.value}>
                        {skill.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!watch('metaSkill') && (
                    <FormHelperText>Please select a meta skill first</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Credly Badge Template ID'
          smallFormTitles={smallFormTitles}
          description='The UUID of the Credly badge template to issue when a user completes this Skill Bite. Find this in the Credly organisation dashboard under each badge template.'
          InputComponent={
            <Input
              control={control}
              label='Credly Badge Template ID'
              name='credlyBadgeTemplateId'
              valid={errors.credlyBadgeTemplateId === undefined}
              showErrors
              error='Must be a valid UUID'
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
    </Grid>
  )
}

export default SkillBiteDetailsForm
